/**
 * @file src/_modulos/prenota/tabela/layouts/TableFooter.tsx
 * @description Footer simplificado da tabela
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza footer
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { useMemo } from "react";
import { useTableStore } from "@PN/tabela";
import type { FetchPrenotasResponse } from "@PN/tabela";
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "visuais";
import {
  FileText,
  Building2,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

/**
 * Props do componente Footer
 */
interface TableFooterProps {
  data?: FetchPrenotasResponse;
  filials?: string[];
  tab?: string;
}

/**
 * Formata número com separadores
 */
function formatNumber(value: number): string {
  return value?.toLocaleString("pt-BR") || "0";
}

/**
 * Obtém label da tab
 */
function getTabLabel(tab: string): string {
  const labels = {
    todas: "pré-notas",
    minhas: "minhas pré-notas",
    pendentes: "pré-notas pendentes",
    revisao: "pré-notas para revisão",
  };
  return labels[tab as keyof typeof labels] || "itens";
}

/**
 * Componente Footer da tabela
 */
export function Footer({ data, filials, tab }: TableFooterProps) {
  const {
    pageSize,
    setPageSize,
    pagination,
    pageIndex,
    setPageIndex,
    activeTab,
    accessibleFilials,
    selectedFilials,
  } = useTableStore();

  const currentTab = tab ?? activeTab;
  const currentFilials = filials ?? selectedFilials;

  // Estatísticas calculadas
  const stats = useMemo(
    () => ({
      totalItems: data?.total ?? 0,
      currentPage: data?.page ?? 1,
      totalPages: data?.totalPages ?? 1,
      pageSize: data?.pageSize ?? 10,
    }),
    [data]
  );

  const filialStats = useMemo(() => {
    const totalAccessible = accessibleFilials.length;
    const selectedCount = currentFilials.length;
    const isAllSelected = selectedCount === totalAccessible;

    return {
      displayText: isAllSelected
        ? "Todas as filiais"
        : `${selectedCount} de ${totalAccessible} filiais`,
      isAllSelected,
    };
  }, [currentFilials, accessibleFilials]);

  const summaryText = useMemo(() => {
    if (stats.totalItems === 0) return "Nenhum item encontrado";

    const tabLabel = getTabLabel(currentTab);
    const pageInfo =
      stats.totalPages > 1
        ? ` (página ${stats.currentPage} de ${stats.totalPages})`
        : "";

    return `${formatNumber(stats.totalItems)} ${tabLabel}${pageInfo}`;
  }, [stats, currentTab]);

  return (
    <div className="flex items-center justify-between w-full gap-4 p-2 px-4">
      {/* Seletor de itens por página */}
      <div className="flex items-center gap-2">
        <Select
          value={String(pageSize)}
          onValueChange={(value) => setPageSize(Number(value))}
        >
          <SelectTrigger className="h-8 w-[80px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 20, 50, 100].map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Estatísticas */}
      <div className="flex items-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <span className="font-medium text-foreground">{summaryText}</span>
          {stats.totalItems > 0 && (
            <Badge
              variant="secondary"
              className="bg-primary/10 text-primary border-primary/20"
            >
              {formatNumber(stats.totalItems)}
            </Badge>
          )}
        </div>

        {stats.totalItems > 0 && (
          <>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-green-500" />
              <Badge
                variant={filialStats.isAllSelected ? "default" : "outline"}
                className="text-xs bg-green-500/10 text-green-600 border-green-500/20"
              >
                {filialStats.displayText}
              </Badge>
            </div>
          </>
        )}
      </div>

      {/* Paginação */}
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setPageIndex(0)}
          disabled={pagination.page === 1}
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => setPageIndex(pageIndex + 1)}
          disabled={pagination.page === pagination.totalPages}
        >
          <ChevronRightIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}
